import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, CalendarDays, Package, User, CheckSquare, MessageSquare, ChevronRight, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { badgeStyle } from "@/lib/badgeColor";
import { toast } from "sonner";

function OutcomeBadge({ label, color }) {
  if (!label) return null;
  return (
    <span style={badgeStyle(color)} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border" data-testid="meeting-outcome-badge">
      {label}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return null;
  try {
    return new Date(d.slice(0, 10)).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return d; }
}

// "Last contacted" hint from the most recent meeting date (local calc, no API).
function lastContactedLabel(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const then = new Date(dateStr.slice(0, 10)); then.setHours(0, 0, 0, 0);
  const days = Math.floor((today - then) / 86400000);
  if (days < 0) return `Last contacted on ${fmtDate(dateStr)}`;
  if (days === 0) return "Last contacted today";
  if (days === 1) return "Last contacted yesterday";
  if (days <= 6) return `Last contacted ${days} days ago`;
  if (days <= 29) {
    const w = Math.floor(days / 7);
    return `Last contacted ${w} week${w === 1 ? "" : "s"} ago`;
  }
  return `Last contacted on ${fmtDate(dateStr)}`;
}

// One per-asset discussion entry. `showAsset` (broker view) vs `showBroker` (asset view).
function EntryRow({ e, showAsset, showBroker }) {
  const navigate = useNavigate();
  return (
    <div className="py-3 border-t border-slate-100 first:border-t-0" data-testid={`meeting-entry-${e.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {showAsset && <Package className="h-4 w-4 text-slate-400 shrink-0" />}
          {showBroker && <User className="h-4 w-4 text-slate-400 shrink-0" />}
          {showAsset && (
            <button onClick={() => navigate(`/assets/${e.asset_id}`)} className="text-sm font-semibold text-slate-900 hover:text-gold text-left truncate" data-testid={`meeting-entry-asset-link-${e.id}`}>
              {e.asset_name}{e.asset_code ? <span className="ml-1.5 text-[11px] text-slate-400 font-mono-num">{e.asset_code}</span> : null}
            </button>
          )}
          {showBroker && (
            <button onClick={() => navigate(`/brokers/${e.broker_id}`)} className="text-sm font-semibold text-slate-900 hover:text-gold text-left truncate" data-testid={`meeting-entry-broker-link-${e.id}`}>
              {e.broker_name}
            </button>
          )}
          {showBroker && e.meeting_date && <span className="text-xs text-slate-500">· {fmtDate(e.meeting_date)}</span>}
        </div>
        <OutcomeBadge label={e.outcome_label} color={e.outcome_color} />
      </div>
      {e.discussion_notes && <div className="mt-1.5 text-sm text-slate-700 whitespace-pre-line">{e.discussion_notes}</div>}
      {e.next_action && <div className="mt-1 text-xs text-slate-600"><span className="text-slate-400">Next Action:</span> {e.next_action}</div>}
      <div className="mt-1 flex items-center gap-3 flex-wrap">
        {e.next_followup_date && (
          <span className="text-xs text-slate-600"><span className="text-slate-400">Follow-up:</span> <span className="font-mono-num">{fmtDate(e.next_followup_date)}</span></span>
        )}
        {e.task_id && (
          <button onClick={() => navigate(`/tasks/${e.task_id}`)} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline" data-testid={`meeting-entry-task-${e.id}`}>
            <CheckSquare className="h-3.5 w-3.5" /> Task {e.task_status ? `· ${e.task_status}` : "linked"}
          </button>
        )}
        {!e.discussion_notes && !e.next_action && !e.next_followup_date && !e.outcome_label && <span className="text-xs text-slate-400">—</span>}
      </div>
    </div>
  );
}

// One meeting entry inside an asset card (broker view): date + edit/delete on one
// line, optional general notes, outcome badge, notes, next action, follow-up + task.
function AssetMeetingEntry({ e, onEdit, onDelete }) {
  const navigate = useNavigate();
  return (
    <div className="py-3 border-t border-slate-100 first:border-t-0 first:pt-1" data-testid={`meeting-entry-${e.id}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarDays className="h-4 w-4 text-slate-400 shrink-0" />
          <span className="text-sm font-semibold text-slate-900 font-mono-num">{fmtDate(e.meeting_date)}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" className="h-12 w-12 p-0" onClick={() => onEdit(e)} data-testid={`broker-meeting-edit-${e.meeting_id}`}><Pencil className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" className="h-12 w-12 p-0 text-red-600 hover:text-red-700" onClick={() => onDelete(e)} data-testid={`broker-meeting-delete-${e.meeting_id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {e.general_notes && (
        <div className="mt-1.5 flex items-start gap-1.5 text-xs text-slate-500 italic" data-testid={`meeting-entry-general-notes-${e.id}`}>
          <MessageSquare className="h-3.5 w-3.5 shrink-0 mt-0.5 text-slate-400" />
          <span className="whitespace-pre-line">{e.general_notes}</span>
        </div>
      )}
      {e.outcome_label && <div className="mt-1.5"><OutcomeBadge label={e.outcome_label} color={e.outcome_color} /></div>}
      {e.discussion_notes && <div className="mt-1.5 text-sm text-slate-700 whitespace-pre-line">{e.discussion_notes}</div>}
      {e.next_action && <div className="mt-1 text-xs text-slate-600"><span className="text-slate-400">Next Action:</span> {e.next_action}</div>}
      {(e.next_followup_date || e.task_id) && (
        <div className="mt-1 flex items-center gap-3 flex-wrap">
          {e.next_followup_date && (
            <span className="text-xs text-slate-600"><span className="text-slate-400">Follow-up:</span> <span className="font-mono-num">{fmtDate(e.next_followup_date)}</span></span>
          )}
          {e.task_id && (
            <button onClick={() => navigate(`/tasks/${e.task_id}`)} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline" data-testid={`meeting-entry-task-${e.id}`}>
              <CheckSquare className="h-3.5 w-3.5" /> Task {e.task_status ? `· ${e.task_status}` : "linked"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// One asset card: latest meeting entry expanded, older ones behind a subtle toggle.
function AssetMeetingCard({ c, archived, onEdit, onDelete }) {
  const navigate = useNavigate();
  const [showOlder, setShowOlder] = useState(false);
  const [latest, ...older] = c.entries;
  return (
    <div className={`border rounded-lg overflow-hidden ${archived ? "bg-slate-50 border-slate-300 border-dashed" : "bg-white border-slate-200"}`} data-testid={`broker-meeting-asset-card-${c.asset_id}`}>
      <div className={`flex items-start justify-between gap-2 flex-wrap p-3 border-b border-slate-100 ${archived ? "bg-slate-100/70" : "bg-[#F5F0E8]"}`}>
        <button
          onClick={() => navigate(`/assets/${c.asset_id}`)}
          className="flex items-start gap-2 min-w-0 text-left min-h-[48px]"
          data-testid={`broker-meeting-asset-link-${c.asset_id}`}
        >
          <Package className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
          <span className="min-w-0">
            <span className={`text-sm font-semibold hover:text-gold break-words ${archived ? "text-slate-600" : "text-slate-900"}`}>{c.asset_name}</span>
            {c.asset_code && <span className="ml-1.5 text-[11px] text-slate-400 font-mono-num">{c.asset_code}</span>}
            {archived && <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-200 text-slate-600 align-middle" data-testid={`broker-meeting-archived-label-${c.asset_id}`}>Archived · {c.assetStatus}</span>}
            {c.latestDate && <span className="block text-[11px] text-slate-400 mt-0.5" data-testid={`broker-meeting-last-contacted-${c.asset_id}`}>{lastContactedLabel(c.latestDate)}</span>}
          </span>
        </button>
        {c.latestOutcomeLabel && <OutcomeBadge label={c.latestOutcomeLabel} color={c.latestOutcomeColor} />}
      </div>
      <div className="px-3 pb-1">
        <AssetMeetingEntry e={latest} onEdit={onEdit} onDelete={onDelete} />
        {showOlder && older.map((e) => (
          <AssetMeetingEntry key={e.id} e={e} onEdit={onEdit} onDelete={onDelete} />
        ))}
        {older.length > 0 && (
          <button
            onClick={() => setShowOlder((v) => !v)}
            className="w-full flex items-center gap-1.5 py-3 min-h-[48px] text-xs text-slate-500 hover:text-slate-700 border-t border-slate-100"
            data-testid={`broker-meeting-older-toggle-${c.asset_id}`}
          >
            {showOlder ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showOlder ? "Hide" : `${older.length}`} earlier meeting{older.length === 1 ? "" : "s"}
          </button>
        )}
      </div>
    </div>
  );
}

const ARCHIVED_ASSET_STATUSES = ["Won", "Lost", "Closed", "Expired"];

// ---- Broker Detail → "Meetings" tab: one card PER ASSET (asset-based grouping) ----
export function BrokerMeetingsTab({ brokerId }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showArchived, setShowArchived] = useState(false);
  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ["broker-meetings", brokerId],
    queryFn: async () => (await api.get("/meetings", { params: { broker_id: brokerId } })).data,
  });

  const del = useMutation({
    mutationFn: async (m) => (await api.delete(`/meetings/${m.id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["broker-meetings", brokerId] });
      toast.success("Meeting deleted");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed to delete"),
  });

  const confirmDelete = (m) => {
    const linked = (m.assets || []).filter((a) => a.task_id).length;
    const msg = linked
      ? `This meeting has ${linked} linked task(s) — deleting the meeting will not delete the tasks. They will remain in your Task module.\n\nDelete this meeting?`
      : "Delete this meeting?";
    if (window.confirm(msg)) del.mutate(m);
  };

  // Regroup meeting→asset entries into one card per asset. Pure display; no data change.
  const { active, archivedCards } = useMemo(() => {
    const groups = {};
    for (const m of meetings) {
      for (const a of (m.assets || [])) {
        if (!groups[a.asset_id]) {
          groups[a.asset_id] = { asset_id: a.asset_id, asset_name: a.asset_name, asset_code: a.asset_code, assetStatus: a.asset_status, entries: [] };
        }
        groups[a.asset_id].entries.push({
          ...a, meeting_id: m.id, meeting_date: m.meeting_date, general_notes: m.general_notes, _meeting: m,
        });
      }
    }
    const cards = Object.values(groups);
    for (const c of cards) {
      c.entries.sort((x, y) => (y.meeting_date || "").localeCompare(x.meeting_date || ""));
      c.latestDate = c.entries[0]?.meeting_date || "";
      c.latestOutcomeLabel = c.entries[0]?.outcome_label;
      c.latestOutcomeColor = c.entries[0]?.outcome_color;
      c.isArchived = ARCHIVED_ASSET_STATUSES.includes(c.assetStatus);
    }
    cards.sort((a, b) => {
      const d = (b.latestDate || "").localeCompare(a.latestDate || "");
      return d !== 0 ? d : (a.asset_name || "").localeCompare(b.asset_name || "");
    });
    return { active: cards.filter((c) => !c.isArchived), archivedCards: cards.filter((c) => c.isArchived) };
  }, [meetings]);

  if (isLoading) return <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>;
  if (meetings.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-10 text-center bg-white border border-slate-200 rounded-md" data-testid="broker-meetings-empty">
        No meetings logged yet. Tap <span className="font-medium text-slate-700">Log Meeting</span> to record your first broker meeting.
      </div>
    );
  }

  const N = archivedCards.length;
  return (
    <div className="space-y-2.5" data-testid="broker-meetings-tab">
      {active.map((c) => (
        <AssetMeetingCard key={c.asset_id} c={c} onEdit={(x) => navigate(`/meetings/${x.meeting_id}/edit`)} onDelete={(x) => confirmDelete(x._meeting)} />
      ))}
      {active.length === 0 && (
        <div className="text-sm text-slate-500 py-8 text-center bg-white border border-slate-200 rounded-md" data-testid="broker-meetings-no-active">
          No active asset discussions. All discussions are for archived assets.
        </div>
      )}
      {N > 0 && (
        <>
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="w-full flex items-center justify-center gap-1.5 py-3 min-h-[48px] text-xs text-slate-500 hover:text-slate-700"
            data-testid="broker-meetings-archived-toggle"
          >
            {showArchived ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {showArchived ? "Hide" : "Show"} archived asset discussions ({N})
          </button>
          {showArchived && (
            <div className="space-y-2.5" data-testid="broker-meetings-archived-section">
              {archivedCards.map((c) => (
                <AssetMeetingCard key={c.asset_id} c={c} archived onEdit={(x) => navigate(`/meetings/${x.meeting_id}/edit`)} onDelete={(x) => confirmDelete(x._meeting)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Asset Detail → "Broker Discussions" tab (read-only, flat, newest first) ----
export function AssetDiscussionsTab({ assetId }) {
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["asset-discussions", assetId],
    queryFn: async () => (await api.get("/meetings/by-asset", { params: { asset_id: assetId } })).data,
  });

  if (isLoading) return <div className="text-sm text-slate-500 py-6 text-center">Loading…</div>;
  if (entries.length === 0) {
    return (
      <div className="text-sm text-slate-500 py-10 text-center bg-white border border-slate-200 rounded-md" data-testid="asset-discussions-empty">
        No broker discussions yet. Log a meeting from any broker's page to record discussions about this asset.
      </div>
    );
  }
  return (
    <div className="bg-white border border-slate-200 rounded-md px-4" data-testid="asset-discussions-tab">
      {entries.map((e) => <EntryRow key={e.id} e={e} showBroker />)}
    </div>
  );
}
